import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// El mismo número de la landing.
const WHATSAPP_NUMERO = '5492216252996';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Por favor ingresá tu email');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setSubmitted(true);
      } else {
        // El servidor explica el problema en `detail` (por ejemplo, si no puede
        // mandar mails): se muestra tal cual, así la persona sabe qué hacer.
        toast.error(data.detail || data.message || 'Error al procesar la solicitud');
      }
    } catch (error) {
      toast.error('Error de conexión. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#1F3A5F]">
              Revisá tu email
            </CardTitle>
            <CardDescription className="text-base">
              Si existe una cuenta con <strong>{email}</strong>, recibirás un enlace para restablecer tu contraseña.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              El enlace vale por 24 horas. Si en unos minutos no te llega, revisá spam o{' '}
              <a
                href={`https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent('Hola, no me llega el mail para recuperar la contraseña de EVALPRO')}`}
                className="text-[#1F3A5F] font-medium underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                escribinos por WhatsApp
              </a>.
            </p>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al inicio de sesión
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#1F3A5F]/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-[#1F3A5F]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1F3A5F]">
            ¿Olvidaste tu contraseña?
          </CardTitle>
          <CardDescription>
            Ingresá tu email y te enviaremos un enlace para crear una nueva contraseña.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                data-testid="forgot-email"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#1F3A5F] hover:bg-[#2d4a6f]"
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar enlace de recuperación'
              )}
            </Button>

            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-slate-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al inicio de sesión
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
